/**
 * Infinity Scroll - Hybrid Mode
 * Shows "LOAD MORE ITEMS" button first, then enables auto-scroll after click
 */
class InfinityScroll extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.autoScrollEnabled = false;
    this.container = "#product-grid";
    this.sectionId = this.dataset.sectionId;
    this.handleClick = this.onClick.bind(this);
    this.observer = null;
  }

  connectedCallback() {
    // Show button first - user must click to start
    this.addEventListener("click", this.handleClick);
    this.updateVisibility();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  updateVisibility() {
    const nextPage = this.getAttribute("data-next-page");
    if (!nextPage || nextPage === "") {
      this.setAttribute("hidden", true);
    } else {
      this.removeAttribute("hidden");
    }
  }

  onClick(e) {
    e.preventDefault();
    if (this.loading) return;

    // Fetch more products
    this.fetchMore();

    // Enable auto-scroll after first click
    if (!this.autoScrollEnabled) {
      this.autoScrollEnabled = true;
      this.initAutoScroll();
      console.log("InfinityScroll enabled - products will now load automatically");
    }
  }

  initAutoScroll() {
    // Set up IntersectionObserver for auto-loading
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.autoScrollEnabled && !this.loading) {
            this.fetchMore();
          }
        });
      },
      { rootMargin: "0px 0px 300px 0px" }
    );
    this.observer.observe(this);
  }

  fetchMore() {
    const nextPageUrl = this.getAttribute("data-next-page");
    if (!nextPageUrl || nextPageUrl === "") {
      this.updateVisibility();
      return;
    }

    console.log("Fetching more products...");
    var url = new URL(nextPageUrl, window.location.origin);
    if (this.sectionId) {
      url.searchParams.append("section_id", this.sectionId);
    }

    this.loading = true;
    this.classList.add("loading");

    fetch(url)
      .then((response) => response.text())
      .then((htmlText) => {
        var html = new DOMParser().parseFromString(htmlText, "text/html");
        const container = document.querySelector(this.container);
        const products = html.querySelector(this.container);

        window.history.replaceState(
          { path: nextPageUrl },
          "",
          nextPageUrl
        );

        if (container && products) {
          // Add fade-in animation to new products
          const newItems = products.querySelectorAll(':scope > li');
          newItems.forEach((item, index) => {
            item.classList.add('load-more-fade-in');
            item.style.setProperty('--animation-order', container.querySelectorAll(':scope > li').length + index + 1);
          });

          container.insertAdjacentHTML("beforeend", products.innerHTML);

          // Trigger fade-in animation
          requestAnimationFrame(() => {
            const freshItems = container.querySelectorAll('.load-more-fade-in:not(.load-more-visible)');
            freshItems.forEach(item => item.classList.add('load-more-visible'));
          });
        }

        const newNextPage = html
          .querySelector("infinity-scroll")
          ?.getAttribute("data-next-page");

        if (newNextPage) {
          this.setAttribute("data-next-page", newNextPage);
        } else {
          this.setAttribute("data-next-page", "");
          this.setAttribute("hidden", true);
          if (this.observer) this.observer.disconnect();
        }

        // Update count text if present
        this.updateCountText(html);
      })
      .catch((error) => {
        console.error("Error fetching more products:", error);
        this.setAttribute("hidden", true);
        if (this.observer) this.observer.disconnect();
      })
      .finally(() => {
        this.loading = false;
        this.classList.remove("loading");
        this.updateVisibility();
      });
  }

  updateCountText(html) {
    const newCount = html.querySelector('.load-more-count');
    const currentCount = document.querySelector('.load-more-count');
    if (newCount && currentCount) {
      currentCount.innerHTML = newCount.innerHTML;
    }
  }
}

customElements.define("infinity-scroll", InfinityScroll);
